import { api } from "#web/services/api";
import { AvailableLeakedCodeSelection, LeakedCodeRecord } from "@gadget-client/keepcart-server";
import * as v from "valibot";

import { LeakedCode } from "../_models/leakedCode";
import { LeakedCodeCounts, LeakedCodePage, LeakedCodePageInput, LeakedCodeReader } from "./service";

export const gadgetLeakedCodeReader = (): LeakedCodeReader => {
  const selection = {
    code: true,
    codeEndsAt: true,
    codeStartsAt: true,
    createdAt: true,
    id: true,
    isCurrentlyLeaked: true,
    isHighRisk: true,
    lastScrapedAt: true,
    leakedSiteMetadata: true,
    leakSources: true,
    shop: {
      createdAt: true,
    },
    shopId: true,
    shopifyDiscountCodeId: true,
    shopifyPriceRuleId: true,
    usageCount: true,
    value: true,
    valueType: true,
  } satisfies AvailableLeakedCodeSelection;

  const fromRecord = (record: LeakedCodeRecord<typeof selection>) => {
    const tolerance = 3 * 24 * 60 * 60 * 1000;
    const leakedOnAppInstallation =
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      (record.createdAt ? new Date(record.createdAt).getTime() : 0) <=
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      (record.shop.createdAt ? new Date(record.shop.createdAt).getTime() : 0) + tolerance;

    return v.parse(LeakedCode, {
      code: record.code!,
      codeEndsAt: record.codeEndsAt,
      codeStartsAt: record.codeStartsAt,
      id: record.id,
      isCurrentlyLeaked: !!record.isCurrentlyLeaked,
      isHighRisk: !!record.isHighRisk,
      lastScrapedAt: record.lastScrapedAt!,
      leakedAt: record.createdAt,
      leakedOnAppInstallation,
      leakedSiteMetadata: record.leakedSiteMetadata as null | Record<string, unknown>,
      shopId: record.shopId,
      shopifyDiscountCodeId: record.shopifyDiscountCodeId ? String(record.shopifyDiscountCodeId) : null,
      shopifyPriceRuleId: record.shopifyPriceRuleId ? String(record.shopifyPriceRuleId) : null,
      sources: record.leakSources
        ? Object.entries(record.leakSources as Record<string, string>).map(([name, url]) => ({ name, url }))
        : [],
      value: record.value ?? 0,
      valueType: record.valueType,
    } satisfies LeakedCode);
  };

  const byId = async (id: string) => {
    const leakedCodeRecord = async () => {
      try {
        return await api.leakedCode.findOne(id, {
          select: selection,
        });
      } catch (unknown) {
        console.error(unknown);
        throw unknown;
      }
    };

    const record = await leakedCodeRecord();

    return fromRecord(record);
  };

  const page = async (input: LeakedCodePageInput) => {
    const leakedCodeRecords = async () => {
      try {
        return await api.leakedCode.findMany({
          after: input.after,
          before: input.before,
          filter: input.filter,
          first: 50,
          search: input.search,
          select: selection,
          sort: { createdAt: "Descending" },
        });
      } catch (unknown) {
        console.error(unknown);
        throw unknown;
      }
    };

    const records = await leakedCodeRecords();

    return v.parse(LeakedCodePage, {
      endCursor: records.endCursor,
      hasNextPage: records.hasNextPage,
      hasPreviousPage: records.hasPreviousPage,
      leakedCodes: records.map(fromRecord),
      startCursor: records.startCursor,
    } satisfies LeakedCodePage);
  };

  const counts = async () => {
    const computedFields = async () => {
      try {
        return await api.shopifyShop.findFirst({
          select: {
            couponExtensionLeakedCodeCount: true,
            currentlyLeakedCodeCount: true,
            highRiskLeakedCodeCount: true,
            inactiveLeakedCodeCount: true,
          },
        });
      } catch (unknown) {
        console.error(unknown);
        throw unknown;
      }
    };

    const result = await computedFields();

    return v.parse(LeakedCodeCounts, {
      // @ts-expect-error gelly fields are not typed yet
      active: result.currentlyLeakedCodeCount.value,
      // @ts-expect-error gelly fields are not typed yet
      couponExtension: result.couponExtensionLeakedCodeCount.value,
      // @ts-expect-error gelly fields are not typed yet
      highRisk: result.highRiskLeakedCodeCount.value,
      // @ts-expect-error gelly fields are not typed yet
      inactive: result.inactiveLeakedCodeCount.value,
    } satisfies LeakedCodeCounts);
  };

  const allExisting = async () => {
    let allRecords = [];
    let currentPage = await api.leakedCode.findMany({
      first: 250,
      select: selection,
    });
    allRecords = [...currentPage];

    while (currentPage.hasNextPage) {
      currentPage = await api.leakedCode.findMany({
        after: currentPage.endCursor,
        filter: {
          shopifyDiscountCodeId: {
            isSet: true,
          },
        },
        first: 250,
        select: selection,
      });
      allRecords = [...allRecords, ...currentPage];
    }

    return v.parse(v.object({ leakedCodes: v.array(LeakedCode) }), {
      leakedCodes: allRecords.map(fromRecord),
    });
  };

  return {
    allExisting,
    byId,
    counts,
    page,
  };
};
